import { useState } from 'react';
import { DateTime } from 'luxon';

import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Sheet } from '@/components/Sheet';
import { BLOCKER_KINDS, blockerTitle, type Blocker, type BlockerKind } from './types';

interface Props {
  blocker: Blocker;
  timezone: string;
  onClose: () => void;
  onSave: (patch: { kind: BlockerKind; label: string | null; starts_at: string; ends_at: string }) => void;
  onDelete: () => void;
  saving?: boolean;
}

/**
 * Edit one blocker: what it is, what to call it, when it runs.
 *
 * Opened on drop for a generic blocker (the "pick what this is" step) and on
 * click for an existing one, so there is one screen to learn rather than two.
 */
export function BlockerSheet({
  blocker,
  timezone,
  onClose,
  onSave,
  onDelete,
  saving,
}: Props) {
  const startLocal = DateTime.fromISO(blocker.starts_at, { zone: 'utc' }).setZone(timezone);
  const endLocal = DateTime.fromISO(blocker.ends_at, { zone: 'utc' }).setZone(timezone);

  const [kind, setKind] = useState<BlockerKind>(blocker.kind);
  const [label, setLabel] = useState(blocker.label ?? '');
  const [start, setStart] = useState(startLocal.toFormat("yyyy-MM-dd'T'HH:mm"));
  const [minutes, setMinutes] = useState(
    String(Math.max(5, Math.round(endLocal.diff(startLocal, 'minutes').minutes))),
  );

  const parsedStart = DateTime.fromISO(start, { zone: timezone });
  const duration = Number(minutes);
  const valid = parsedStart.isValid && Number.isFinite(duration) && duration >= 5;

  return (
    <Sheet
      title={blockerTitle({ kind, label: label.trim() || null })}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={onDelete}>
            Remove
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              loading={saving}
              disabled={!valid}
              onClick={() => {
                if (!valid) return;
                onSave({
                  kind,
                  label: label.trim() || null,
                  starts_at: parsedStart.toUTC().toISO()!,
                  ends_at: parsedStart.plus({ minutes: duration }).toUTC().toISO()!,
                });
              }}
            >
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label
            htmlFor="blocker-kind"
            className="mb-2 block text-small font-medium text-text-secondary"
          >
            What is it
          </label>
          <select
            id="blocker-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as BlockerKind)}
            className="min-h-touch w-full rounded border border-border bg-surface-1 px-2 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
          >
            {BLOCKER_KINDS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <Field
          label="Name"
          hint="Optional: leave blank to use the kind above."
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />

        <Field
          label="Start"
          type="datetime-local"
          value={start}
          onChange={(e) => setStart(e.target.value)}
        />

        <Field
          label="Minutes"
          type="number"
          min={5}
          step={5}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
        />
      </div>

      <p className="mt-4 text-small text-text-tertiary">
        Blockers make the time busy so nothing gets scheduled on top. They never
        count as work on the Daily page.
      </p>
    </Sheet>
  );
}
